import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { projectsAPI, integrationsAPI, type OAuthGroup } from '../services/api';
import type {
  Project,
  NewProjectFormData,
  IntegrationProvider,
} from '../types/project';

interface ProjectsContextType {
  // State
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;

  // Project operations
  fetchProjects: () => Promise<void>;
  createProject: (data: NewProjectFormData) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
  selectProject: (id: string | null) => void;

  // Competitor operations
  addCompetitor: (projectId: string, url: string) => Promise<void>;
  removeCompetitor: (projectId: string, competitorId: string) => Promise<void>;

  // Integration operations
  connectIntegration: (projectId: string, provider: IntegrationProvider) => Promise<string>;
  disconnectIntegration: (projectId: string, integrationId: string) => Promise<void>;
  refreshIntegration: (projectId: string, integrationId: string) => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

const STORAGE_PREFIX = 'agentic_seo';

export function ProjectsProvider({ children }: { children: React.ReactNode }) {
  const { apiKey, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(() => {
    return localStorage.getItem(`${STORAGE_PREFIX}_current_project`);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Derived current project
  const currentProject = useMemo(() => {
    if (!currentProjectId) return null;
    return projects.find((p) => p.id === currentProjectId) || null;
  }, [projects, currentProjectId]);

  // Persist current project selection
  useEffect(() => {
    if (currentProjectId) {
      localStorage.setItem(`${STORAGE_PREFIX}_current_project`, currentProjectId);
    } else {
      localStorage.removeItem(`${STORAGE_PREFIX}_current_project`);
    }
  }, [currentProjectId]);

  // Fetch projects on auth change
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await projectsAPI.list(apiKey);
      setProjects(data);

      // Auto-select first project if none selected
      if (!currentProjectId && data.length > 0) {
        setCurrentProjectId(data[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, currentProjectId, isAuthenticated]);

  // Load projects when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects();
    } else {
      setProjects([]);
      setCurrentProjectId(null);
    }
  }, [isAuthenticated, apiKey]);

  const createProject = useCallback(
    async (data: NewProjectFormData): Promise<Project> => {
      if (!isAuthenticated) throw new Error('Session expired. Please log in again.');

      setIsLoading(true);
      try {
        const project = await projectsAPI.create(apiKey, data);
        setProjects((prev) => [...prev, project]);
        setCurrentProjectId(project.id);
        return project;
      } finally {
        setIsLoading(false);
      }
    },
    [apiKey]
  );

  const updateProject = useCallback(
    async (id: string, data: Partial<Project>): Promise<Project> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      const updated = await projectsAPI.update(apiKey, id, data);
      setProjects((prev) => prev.map((p) => (p.id === id ? updated : p)));
      return updated;
    },
    [apiKey]
  );

  const deleteProject = useCallback(
    async (id: string): Promise<void> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      await projectsAPI.delete(apiKey, id);
      setProjects((prev) => prev.filter((p) => p.id !== id));

      if (currentProjectId === id) {
        const remaining = projects.filter((p) => p.id !== id);
        setCurrentProjectId(remaining.length > 0 ? remaining[0].id : null);
      }
    },
    [apiKey, currentProjectId, projects]
  );

  const selectProject = useCallback((id: string | null) => {
    setCurrentProjectId(id);
  }, []);

  // Competitor operations
  const addCompetitor = useCallback(
    async (projectId: string, url: string): Promise<void> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      const competitor = await projectsAPI.addCompetitor(apiKey, projectId, url);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, competitors: [...p.competitors, competitor] } : p
        )
      );
    },
    [apiKey]
  );

  const removeCompetitor = useCallback(
    async (projectId: string, competitorId: string): Promise<void> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      await projectsAPI.removeCompetitor(apiKey, projectId, competitorId);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, competitors: p.competitors.filter((c) => c.id !== competitorId) }
            : p
        )
      );
    },
    [apiKey]
  );

  // Integration operations
  const connectIntegration = useCallback(
    async (_projectId: string, provider: IntegrationProvider): Promise<string> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      // Map IntegrationProvider to OAuthGroup
      const providerPrefix = provider.split('_')[0];
      const groupMap: Record<string, OAuthGroup> = {
        google: 'google', youtube: 'google',
        microsoft: 'microsoft', bing: 'microsoft',
        meta: 'meta', tiktok: 'tiktok', linkedin: 'linkedin',
      };
      const providerGroup = groupMap[providerPrefix];
      if (!providerGroup) throw new Error(`Unknown provider group for: ${provider}`);

      const { authorizationUrl } = await integrationsAPI.initiateOAuth(apiKey, providerGroup);
      return authorizationUrl;
    },
    [apiKey]
  );

  const disconnectIntegration = useCallback(
    async (projectId: string, integrationId: string): Promise<void> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      await integrationsAPI.removeFromProject(apiKey, projectId, integrationId);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, integrations: p.integrations.filter((i) => i.id !== integrationId) }
            : p
        )
      );
    },
    [apiKey]
  );

  const refreshIntegration = useCallback(
    async (projectId: string, integrationId: string): Promise<void> => {
      if (!isAuthenticated) throw new Error('Not authenticated');

      // Find the integration's OAuth connection and refresh it
      const project = projects.find((p) => p.id === projectId);
      const integration = project?.integrations.find((i) => i.id === integrationId);
      if (integration?.oauthConnectionId) {
        await integrationsAPI.refreshOAuth(apiKey, integration.oauthConnectionId);
      }

      // Re-fetch project integrations to get updated status
      const updatedIntegrations = await integrationsAPI.listProjectIntegrations(apiKey, projectId);
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? { ...p, integrations: updatedIntegrations }
            : p
        )
      );
    },
    [apiKey, projects]
  );

  const value = useMemo(
    () => ({
      projects,
      currentProject,
      isLoading,
      error,
      fetchProjects,
      createProject,
      updateProject,
      deleteProject,
      selectProject,
      addCompetitor,
      removeCompetitor,
      connectIntegration,
      disconnectIntegration,
      refreshIntegration,
    }),
    [
      projects,
      currentProject,
      isLoading,
      error,
      fetchProjects,
      createProject,
      updateProject,
      deleteProject,
      selectProject,
      addCompetitor,
      removeCompetitor,
      connectIntegration,
      disconnectIntegration,
      refreshIntegration,
    ]
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
}
